import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Input, Space, Typography, Spin, Alert } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import AgentMessageBubble from './AgentMessageBubble';
import { sendAgentMessage } from '../../api/agentApi';
import type {
  AgentMessage,
  AgentDatosExtraidos,
} from '../../types/agent';

const { Text } = Typography;
const { TextArea } = Input;

interface AgentChatProps {
  sessionId: string;
  clienteId?: number;
  onComplete: (messages: AgentMessage[], datosExtraidos: AgentDatosExtraidos) => void;
}

const AgentChat: React.FC<AgentChatProps> = ({
  sessionId,
  clienteId,
  onComplete,
}) => {
  // Mensaje de bienvenida local (sin llamada al backend)
  const welcomeMessage: AgentMessage = {
    role: 'agent',
    text: clienteId
      ? `Cliente ID: ${clienteId}. Puedes empezar a dictar la actividad.`
      : 'No se ha indicado cliente. Puedes empezar a dictar la actividad y mencionar el cliente en el relato.',
  };

  const [messages, setMessages] = useState<AgentMessage[]>([welcomeMessage]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Focus en el input al montar
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMessage: AgentMessage = { role: 'user', text };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const agentResponse = await sendAgentMessage({ sessionId, clienteId: clienteId ?? 0, message: text });

      if (agentResponse.estado === 'completado') {
        const confirmText =
          'He recogido toda la información necesaria. Revisa el resumen antes de confirmar el envío.';
        setMessages((prev) => [
          ...prev,
          { role: 'agent', text: confirmText },
        ]);
        // Pequeño delay para que el usuario vea el mensaje antes de cambiar de fase
        setTimeout(() => {
          onComplete(
            [...messages, userMessage, { role: 'agent', text: confirmText }],
            agentResponse.datos_extraidos
          );
        }, 800);
      } else {
        const agentText =
          agentResponse.siguiente_pregunta ?? '¿Hay algo más que quieras añadir?';
        setMessages((prev) => [
          ...prev,
          { role: 'agent', text: agentText },
        ]);
      }
    } catch {
      setError('Error al comunicarse con el agente. Inténtalo de nuevo.');
      // Revertir el mensaje del usuario si la llamada falló
      setMessages((prev) => prev.slice(0, -1));
      setInput(text);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, loading, messages, onComplete, sessionId, clienteId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={0}>
      {/* Historial de mensajes */}
      <div
        style={{
          minHeight: 320,
          maxHeight: 480,
          overflowY: 'auto',
          padding: '8px 0',
          marginBottom: 12,
        }}
      >
        {messages.map((msg, i) => (
          <AgentMessageBubble key={i} message={msg} />
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
            <Spin size="small" style={{ marginLeft: 8 }} />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error */}
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 8 }}
        />
      )}

      {/* Input */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <TextArea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Dicta o escribe la actividad… (Enter para enviar, Shift+Enter para nueva línea)"
          autoSize={{ minRows: 2, maxRows: 5 }}
          disabled={loading}
          style={{ flex: 1 }}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          disabled={!input.trim() || loading}
          style={{ height: 'auto', minHeight: 56 }}
        />
      </div>

      <Text type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
        Enter para enviar · Shift+Enter para nueva línea
      </Text>
    </Space>
  );
};

export default AgentChat;
